import React, {useEffect, useState} from 'react';
import {Box, Container, styled} from '@mui/material';
import Typography from '@mui/material/Typography';
import {useOutletContext, useParams} from 'react-router-dom';
import {
    downloadSchoolDocument,
    useGetSchoolDocumentsQuery,
    useSyncStaffDocumentToSimsMutation,
} from '../../../api/services/filesService';
import useFileDownloader from '../../../utility/hooks/useFileDownloader';
import useNotification from '../../../utility/hooks/useNotification';
import DocumentList from '../Profiles/Components/DocumentList';

const DocumentFields = styled(Container)({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'start',
    padding: 0
});

const EmployeeProfileDocumentsTab = () => {
    const {id: routeEmployeeId} = useParams();
    const {data: employee} = useOutletContext() || {};
    const employeeId = routeEmployeeId ? Number(routeEmployeeId) : employee?.id;
    const [schoolDocuments, setSchoolDocuments] = useState([]);
    const [paginationModel, setPaginationModel] = useState({
        pageNumber: 0,
        pageSize: 20,
        hasMore: true
    });

    const {
        data: schoolDocumentsPage,
        isLoading: isSchoolDocumentsLoading
    } = useGetSchoolDocumentsQuery(
        {
            ...paginationModel,
            employeeId: Number(employeeId)
        },
        {skip: !employeeId}
    );

    const downloadFile = useFileDownloader();
    const {showErrorNotification, showSuccessNotification} = useNotification();
    const [syncToSims] = useSyncStaffDocumentToSimsMutation();

    // AD-2789: only a School Admin opens a member of staff's profile, so the profile route (with the member of
    // staff's id) is where a document can be sent to SIMS. MY PROFILE has no id in its route.
    const syncDocumentToSims = async document => {
        try {
            await syncToSims({employeeId: Number(employeeId), documentId: document.id}).unwrap();
            setSchoolDocuments(current => current.map(item => item.id === document.id
                ? {...item, sentToSimsAt: new Date().toISOString()}
                : item));
            showSuccessNotification(`${document.name} will be added to Linked Documents on the SIMS staff record at the next sync.`);
        } catch (error) {
            showErrorNotification(typeof error?.data === 'string' ? error.data : 'The document could not be sent to SIMS.');
        }
    };

    useEffect(() => {
        setSchoolDocuments([]);
        setPaginationModel({
            pageNumber: 0,
            pageSize: 20,
            hasMore: true
        });
    }, [employeeId]);

    useEffect(() => {
        if (schoolDocumentsPage && !isSchoolDocumentsLoading) {
            setSchoolDocuments(current => [...current, ...schoolDocumentsPage.items]);
            setPaginationModel(current => ({
                ...current,
                hasMore: schoolDocumentsPage.items.length > 0
            }));
        }
    }, [schoolDocumentsPage, isSchoolDocumentsLoading]);

    const handleScrollSchoolDocumentList = event => {
        const {scrollTop, clientHeight, scrollHeight} = event.currentTarget;

        if (paginationModel.hasMore &&
            scrollHeight - scrollTop === clientHeight &&
            !isSchoolDocumentsLoading
        ) {
            setPaginationModel(current => ({
                ...current,
                pageNumber: current.pageNumber + 1
            }));
        }
    };

    const employeeName = employee
        ? `${employee.legalForename || employee.preferredForename || ''} ${employee.legalSurname || employee.preferredSurname || ''}`.trim()
        : 'you';

    const documentsMap = [
        {
            sectionName: 'School Documents',
            documents: schoolDocuments,
            paginationHandler: handleScrollSchoolDocumentList,
            onDownload: async documentId => await downloadFile(
                downloadSchoolDocument,
                {documentId, employeeId: Number(employeeId)}
            ),
            // 'View' opens the document in the preview pop-up, which has 'Download', as on the child's profile.
            onFetch: documentId => downloadSchoolDocument({documentId, employeeId: Number(employeeId)}),
            onSyncToSims: routeEmployeeId ? syncDocumentToSims : undefined
        }
    ];

    if (!employeeId) {
        return null;
    }

    return (
        <DocumentFields>
            {schoolDocuments.length === 0 && !isSchoolDocumentsLoading
                ? (
                    <Box display={'flex'} justifyContent={'center'} py={2}>
                        <Typography color={'silver'} variant={'h6'}>
                            No documents available for {employeeName}
                        </Typography>
                    </Box>
                )
                : null}
            <DocumentList documents={documentsMap} staffProfile/>
        </DocumentFields>
    );
};

export default EmployeeProfileDocumentsTab;
